import { useAuth } from '@clerk/clerk-react';
import { ViewPlaceholder } from '@vrplatform/components';
import {
  useMe,
  useOwnerPortalContact,
  useQuery,
  useTeam,
  useTeamIdContext,
  useTeamSwitch,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { sortBy } from '@vrplatform/utils';
import {
  Anchor,
  Card,
  Center,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  rem,
} from '@mantine/core';
import {
  formatAddress,
  formatOwnerName,
  getOwnerAddress,
} from '@vrplatform/ui-common';
import { type ReactNode, Suspense } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { VerifyTokenView } from '../../views/invite-redirect/VerifyTokenView';
import { Layout } from '../../views/onboarding/components';
import { getOwnerPortalSettings } from '../../views/settings/SettingsFeaturesOwnerPortal';
import { lazyWithRetry } from './_utils';
import {
  getOwnerAccessWhere,
  isOwnerPortalImpersonating,
  isOwnerPortalTeamSwitchPending,
} from './OwnerRoutes.utils';
import { OwnerStatementSummariesDetailView } from '../../views/general-ledger/owner-statement-summaries/detail';

const OwnerReservationsLegacyView = lazyWithRetry(
  () => import('../../views/reservations/legacy/OwnerReservationsView')
);

const OwnerReservationsView = lazyWithRetry(
  () => import('../../views/reservations/ledger/OwnerReservationsView')
);

const OwnerCalendarLegacyView = lazyWithRetry(
  () => import('../../views/reservations/legacy/OwnerCalendarView')
);

const OwnerCalendarView = lazyWithRetry(
  () => import('../../views/reservations/ledger/OwnerCalendarView')
);

const OwnerPortalListingsView = lazyWithRetry(
  () => import('../../views/listings/list/legacy/OwnerPortalListingsListView')
);
const LegacyStatementView = lazyWithRetry(
  () => import('../../views/statements/legacy/statement')
);
const LegacySummaryView = lazyWithRetry(
  () => import('../../views/statements/legacy/summary')
);
const StatementView = lazyWithRetry(
  () => import('../../views/statements/statement')
);

const LegacyStatementListView = lazyWithRetry(
  () =>
    import(
      '../../views/statements/legacy/statement-list/OwnerPortalStatementListView'
    )
);
const StatementListView = lazyWithRetry(
  () =>
    import('../../views/statements/statement-list/OwnerPortalStatementListView')
);

const FilesView = lazyWithRetry(() => import('../../views/files'));

const SettingsView = lazyWithRetry(() => import('../../views/settings'));

export const OwnerRoutes = () => {
  const { actor } = useAuth();

  const { contactId } = useOwnerPortalContact();

  const isUserPreview = !!actor?.sub;

  return (
    <Suspense fallback={<LoadingIndicator isFullPageLoading />}>
      <Routes>
        <Route
          path="token-verify"
          element={<VerifyTokenView isAuthenticated />}
        />

        {!isUserPreview && (
          <Route path="settings/*" element={<SettingsView />} />
        )}

        <Route
          path="*"
          element={contactId ? <OwnerSelected /> : <LedgerOwnerUnselected />}
        />
      </Routes>
    </Suspense>
  );
};

const OnboardingLayoutFooter = () => {
  const { signOut } = useAuth();

  return (
    <Text c="gray" ta="center">
      Not the right account?{' '}
      <Anchor
        inline
        component="button"
        onClick={() => signOut({ redirectUrl: '/' })}
      >
        Log out.
      </Anchor>
    </Text>
  );
};

const OnboardingLayout = ({ children }: { children: ReactNode }) => {
  return (
    <Layout
      teamSelectComponent={null}
      footer={<OnboardingLayoutFooter />}
      activeStep={undefined}
      // menu={<OnboardingMenu />}
      menu={null}
      GL={undefined}
      teamType={'propertyManager'}
      title="Select your profile to login"
      description="You have multiple profiles linked to this account. Choose one to access the owner statements and data."
      sidebar={null}
    >
      {children}
    </Layout>
  );
};

const LedgerOwnerUnselected = () => {
  const { actor } = useAuth();
  const { id: meId } = useMe();
  const [{ id: hydratedTeamId }] = useTeam();
  const { teamId: selectedTeamId } = useTeamIdContext();
  const setTeamId = useTeamSwitch();
  const isUserPreview = !!actor?.sub;
  const [searchParams] = useSearchParams();
  const sti = searchParams.get('sti');
  const teamScopeId = selectedTeamId ?? hydratedTeamId;
  const isImpersonating = isOwnerPortalImpersonating({
    isUserPreview,
    queryTeamId: sti,
  });
  const isPreviewTeamSwitchPending = isOwnerPortalTeamSwitchPending({
    isImpersonating,
    queryTeamId: sti,
    activeTeamId: teamScopeId,
  });

  const { setContactId } = useOwnerPortalContact();

  const queryData = useQuery(
    (q, args) => {
      const where = getOwnerAccessWhere({
        isImpersonating: args.isImpersonating,
        teamId: args.teamId,
        meId: args.meId,
      });

      const access = q
        .ownerUserAccesses({
          where,
        })
        .map((x) => {
          const ownerName = formatOwnerName(x.owner, { showEmpty: true });
          const contactName = formatOwnerName(x.contact, { showEmpty: true });

          const contactAddress = formatAddress(x.contact?.addressData() || {});
          const ownerAddress = getOwnerAddress(x.owner, {
            format: 'owner_statement',
          });

          const contactIsCompany = !!x.contact?.companyType;
          const ownerIsCompany = x.owner?.type === 'company';

          const contactId = x.contactId;
          const contactTenantId = x.contact?.tenantId;
          const ownerId = x.ownerId;
          const ownerTenantId = x.owner?.tenantId;

          const id = contactId || ownerId || null;
          const tenantId = contactTenantId || ownerTenantId;

          const type = contactId ? 'contact' : 'owner';

          return {
            id,
            tenantId,
            type,
            isCompany: type === 'contact' ? contactIsCompany : ownerIsCompany,
            name: ownerName || contactName || '-',
            address: type === 'contact' ? contactAddress : ownerAddress?.full,
          };
        });

      return sortBy(access, 'name');
    },
    {
      variables: {
        meId,
        teamId: teamScopeId,
        isImpersonating,
      },
      skip:
        !meId ||
        (isImpersonating && (!teamScopeId || isPreviewTeamSwitchPending)),
    }
  );

  if (queryData.isFetching)
    return (
      <OnboardingLayout>
        <Stack w="100%" gap="xl">
          <Skeleton h={100} w="100%" />
          <Skeleton h={100} w="100%" />
          <Skeleton h={100} w="100%" />
          <Skeleton h={100} w="100%" />
        </Stack>
      </OnboardingLayout>
    );

  if (queryData.error)
    return (
      <OnboardingLayout>
        <ViewPlaceholder
          title="Failed to load your profiles"
          description={
            queryData.error?.message ||
            'Please reach out support if the issue persists.'
          }
          icon="AlertTriangleIcon"
          iconColor="red"
        />
      </OnboardingLayout>
    );

  return (
    <OnboardingLayout>
      <ScrollArea w="100%" mah={rem(520)} type="auto">
        <Stack w="100%" gap="xl" align="stretch" py="sm">
          {queryData.data?.map((x) => {
            if (!x.id) return null;

            return (
              <Card
                key={x.id}
                withBorder
                component="button"
                onClick={() => {
                  setContactId(x.id);
                  if (x.tenantId) setTeamId(x.tenantId);
                }}
                w="100%"
                sx={(theme) => ({
                  transition: 'all 0.2s ease-in-out',
                  textAlign: 'left',
                  cursor: 'pointer',
                  ':hover': {
                    boxShadow: theme.shadows.sm,
                    svg: {
                      opacity: 1,
                    },
                  },
                })}
              >
                <Group justify="space-between" mb="sm">
                  <Center>
                    {x.isCompany ? <CompanyIcon /> : <IndividualIcon />}
                  </Center>
                  <Icon
                    icon="ChevronIcon"
                    color={(theme) => theme.colors.gray[5]}
                    sx={{
                      transform: 'rotate(-90deg)',
                      opacity: 0,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                </Group>
                <Text fw={500} size="md">
                  {x.name}
                </Text>
                {!!x.address && (
                  <Text c="gray" mt={rem(5)}>
                    {x.address}
                  </Text>
                )}
              </Card>
            );
          })}
        </Stack>
      </ScrollArea>
    </OnboardingLayout>
  );
};

const OwnerSelected = () => {
  const [{ id: teamId, isGL: GL }] = useTeam();
  const { search } = useLocation();

  const { id: meId } = useMe();
  const { contactId } = useOwnerPortalContact();

  const { data } = useQuery(
    (q, args) => {
      const settings = getOwnerPortalSettings(q, args.teamId);

      const isOwnerArchived = !!q
        .ownerUserAccesses({
          where: {
            userId: { _eq: args.meId },
            _or: [
              {
                contactId: { _eq: args.contactId },
                contact: {
                  status: { _eq: 'inactive' },
                },
              },
              {
                ownerId: { _eq: args.contactId },
                owner: {
                  status: { _eq: 'inactive' },
                },
              },
            ],
          },
        })
        .map((x) => x.id)[0];

      return {
        isOwnerArchived,
        showReservationSetting: !!settings.showReservationSettingId,
      };
    },
    {
      variables: {
        teamId,
        meId,
        contactId,
      },
    }
  );

  const isArchived = data?.isOwnerArchived;
  const showReservations = !!data?.showReservationSetting && !isArchived;

  return (
    <Routes>
      <Route index element={<Navigate to={`/statements${search}`} replace />} />

      <Route path="owners" element={<Navigate to={`/statements${search}`} />} />

      {/* Redirect from old path to new */}
      <Route
        path="unit-statements"
        element={<Navigate to={`/statements${search}`} />}
      />

      <Route path="statements">
        <Route index element={<Navigate to="owner-statements" replace />} />

        {!GL && <Route path="summary" element={<LegacySummaryView />} />}

        {GL && (
          <Route
            path="summary/:monthRange"
            element={<OwnerStatementSummariesDetailView />}
          />
        )}

        <Route
          path=":tabValue"
          element={GL ? <StatementListView /> : <LegacyStatementListView />}
        />
      </Route>

      {(showReservations || window.location.host.includes('localhost')) && (
        <>
          <Route
            path="reservations"
            element={
              GL ? <OwnerReservationsView /> : <OwnerReservationsLegacyView />
            }
          />
          <Route
            path="calendar"
            element={GL ? <OwnerCalendarView /> : <OwnerCalendarLegacyView />}
          />
        </>
      )}

      {data && !isArchived && (
        <>
          <Route path="files" index element={<FilesView />} />
          {!GL && (
            <Route path="listings" element={<OwnerPortalListingsView />} />
          )}
        </>
      )}

      <Route path="statement">
        {GL ? (
          <Route path=":periodId" element={<StatementView />} />
        ) : (
          <Route path=":listingId" element={<LegacyStatementView />} />
        )}
      </Route>

      <Route
        path="login"
        element={<Navigate to={`/statements${search}`} replace />}
      />
    </Routes>
  );
};

const IndividualIcon = () => (
  <svg
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.12">
      <path
        d="M4.5 18.8C4.5 16.149 6.64903 14 9.3 14H15.7C18.351 14 20.5 16.149 20.5 18.8C20.5 20.5673 19.0673 22 17.3 22H7.7C5.93269 22 4.5 20.5673 4.5 18.8Z"
        fill="#60A5FA"
      />
      <path
        d="M16.5 6C16.5 8.20914 14.7091 10 12.5 10C10.2909 10 8.5 8.20914 8.5 6C8.5 3.79086 10.2909 2 12.5 2C14.7091 2 16.5 3.79086 16.5 6Z"
        fill="#60A5FA"
      />
    </g>
    <path
      d="M4.5 18.8C4.5 16.149 6.64903 14 9.3 14H15.7C18.351 14 20.5 16.149 20.5 18.8V18.8C20.5 20.5673 19.0673 22 17.3 22H7.7C5.93269 22 4.5 20.5673 4.5 18.8V18.8Z"
      stroke="#1343CC"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 18.8C4.5 16.149 6.64903 14 9.3 14H15.7C18.351 14 20.5 16.149 20.5 18.8V18.8C20.5 20.5673 19.0673 22 17.3 22H7.7C5.93269 22 4.5 20.5673 4.5 18.8V18.8Z"
      stroke="black"
      strokeOpacity="0.2"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.5 6C16.5 8.20914 14.7091 10 12.5 10C10.2909 10 8.5 8.20914 8.5 6C8.5 3.79086 10.2909 2 12.5 2C14.7091 2 16.5 3.79086 16.5 6Z"
      stroke="#1343CC"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.5 6C16.5 8.20914 14.7091 10 12.5 10C10.2909 10 8.5 8.20914 8.5 6C8.5 3.79086 10.2909 2 12.5 2C14.7091 2 16.5 3.79086 16.5 6Z"
      stroke="black"
      strokeOpacity="0.2"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CompanyIcon = () => (
  <svg
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.25 21H7.25V17.625C7.25 17.004 7.754 16.5 8.375 16.5H10.625C11.246 16.5 11.75 17.004 11.75 17.625V21H14.75V7.5V5C14.75 3.89543 13.8546 3 12.75 3H6.25C5.14543 3 4.25 3.89543 4.25 5V21Z"
      fill="#A78BFA"
      fillOpacity="0.12"
    />
    <path
      d="M2.75 21H22.25M14.75 21V7.5M14.75 21H20.75V9.5C20.75 8.39543 19.8546 7.5 18.75 7.5H14.75M14.75 21H11.75V17.625C11.75 17.004 11.246 16.5 10.625 16.5H8.375C7.754 16.5 7.25 17.004 7.25 17.625V21H4.25V5C4.25 3.89543 5.14543 3 6.25 3H12.75C13.8546 3 14.75 3.89543 14.75 5V7.5M7.25 6.75H8M7.25 9.75H8M7.25 12.75H8M11 6.75H11.75M11 9.75H11.75M11 12.75H11.75M17.75 11.25H17.758V11.258H17.75V11.25ZM17.75 14.25H17.758V14.258H17.75V14.25ZM17.75 17.25H17.758V17.258H17.75V17.25Z"
      stroke="#7C3AED"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
